import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../components/common/DataTable';
import { PageHeader } from '../components/common/PageHeader';
import { SearchFilter } from '../components/common/SearchFilter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Edit, Trash2, Plus, Loader2, CheckSquare, Clock, AlertTriangle, Users, FileEdit, ClipboardList } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = ['Project', 'Support', 'Internal', 'Admin', 'Training'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
const STATUSES = ['Not Started', 'In Progress', 'Blocked', 'Completed', 'Cancelled'];

export default function TasksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [activeTab, setActiveTab] = useState('created'); // 'created' or 'assigned'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [creating, setCreating] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ total: 0, overdue: 0, by_status: {}, by_priority: {}, created_count: 0, assigned_count: 0 });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: 'Internal',
    priority: 'Medium',
    assigned_to_ids: [],
    planned_start_date: '',
    planned_end_date: '',
    estimated_hours: 0,
    billable: false,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        filter_type: activeTab,
        employee_id: user?.id || '',
      });
      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      if (priority !== 'all') params.append('priority', priority);

      const response = await axios.get(`${API_URL}/api/tasks?${params}`);
      setTasks(response.data.tasks || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 1,
      }));
    } catch (error) {
      toast.error('Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, status, priority, activeTab, user?.id]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tasks/stats/summary`);
      
      // Also fetch counts for created and assigned tabs
      const [createdRes, assignedRes] = await Promise.all([
        axios.get(`${API_URL}/api/tasks?filter_type=created&employee_id=${user?.id}&limit=1`),
        axios.get(`${API_URL}/api/tasks?filter_type=assigned&employee_id=${user?.id}&limit=1`)
      ]);
      
      setStats({
        ...response.data,
        created_count: createdRes.data.total || 0,
        assigned_count: assignedRes.data.total || 0
      });
    } catch { /* ignore */ }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employees?limit=100`);
      setEmployees(response.data.employees || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchTasks();
    fetchStats();
    fetchEmployees();
  }, [fetchTasks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTask.title || !newTask.description) {
      toast.error('Title and description are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/tasks?created_by=${user?.id}`, newTask);
      toast.success('Task created');
      setShowCreateModal(false);
      setNewTask({
        title: '',
        description: '',
        category: 'Internal',
        priority: 'Medium',
        assigned_to_ids: [],
        planned_start_date: '',
        planned_end_date: '',
        estimated_hours: 0,
        billable: false,
      });
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${task.id}?updated_by=${user?.id}`, { status: newStatus });
      toast.success('Status updated');
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/tasks/${task.id}`);
      toast.success('Task deleted');
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const getPriorityColor = (p) => {
    const colors = {
      Low: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      Medium: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      High: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      Critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[p] || colors.Medium;
  };

  const getStatusColor = (s) => {
    const colors = {
      'Not Started': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      'In Progress': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Blocked': 'bg-red-500/20 text-red-400 border-red-500/30',
      'Completed': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Cancelled': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    };
    return colors[s] || colors['Not Started'];
  };

  // Dynamic columns based on active tab
  const getColumns = () => {
    const baseColumns = [
      {
        key: 'task',
        header: 'Task',
        render: (row) => (
          <div className="max-w-xs">
            <p className="font-medium text-white truncate">{row.title}</p>
            <p className="text-xs text-slate-500 mono">{row.task_id}</p>
          </div>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        render: (row) => <span className="text-slate-300">{row.category}</span>,
      },
      {
        key: 'priority',
        header: 'Priority',
        render: (row) => (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(row.priority)}`}>
            {row.priority}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(row.status)}`}>
            {row.status}
          </span>
        ),
      },
    ];

    // Show "Created By" for allocated tasks, "Assigned To" for created tasks
    if (activeTab === 'assigned') {
      baseColumns.push({
        key: 'created_by',
        header: 'Created By',
        render: (row) => (
          <div className="flex items-center gap-1">
            <FileEdit className="w-3 h-3 text-slate-500" />
            <span className="text-slate-300 text-sm">
              {row.created_by_name || 'Unknown'}
            </span>
          </div>
        ),
      });
    } else {
      baseColumns.push({
        key: 'assigned',
        header: 'Assigned To',
        render: (row) => (
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3 text-slate-500" />
            <span className="text-slate-300 text-sm">
              {row.assigned_to?.length > 0 
                ? row.assigned_to.find(a => a.is_primary)?.employee_name || row.assigned_to[0]?.employee_name
                : 'Unassigned'}
            </span>
          </div>
        ),
      });
    }

    // Add remaining columns
    baseColumns.push(
      {
        key: 'progress',
        header: 'Progress',
        render: (row) => (
          <div className="w-20">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${row.progress}%` }}
                />
              </div>
              <span className="text-xs text-slate-400">{row.progress}%</span>
            </div>
          </div>
        ),
      },
      {
        key: 'due',
        header: 'Due Date',
        render: (row) => {
          const isOverdue = row.planned_end_date && new Date(row.planned_end_date) < new Date() && row.status !== 'Completed';
          return (
            <span className={`text-sm ${isOverdue ? 'text-red-400' : 'text-slate-400'}`}>
              {row.planned_end_date ? formatDate(row.planned_end_date) : '-'}
              {isOverdue && <AlertTriangle className="w-3 h-3 inline ml-1" />}
            </span>
          );
        },
      },
      {
        key: 'actions',
        header: '',
        render: (row) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`task-actions-${row.id}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
              <DropdownMenuItem 
                onClick={() => { setSelectedTask(row); setShowDetailModal(true); }}
                className="text-slate-300 focus:bg-slate-800 focus:text-white"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
              {row.status !== 'Completed' && (
                <DropdownMenuItem 
                  onClick={() => handleStatusChange(row, 'In Progress')}
                  className="text-blue-400 focus:bg-blue-500/10"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Start Task
                </DropdownMenuItem>
              )}
              {row.status === 'In Progress' && (
                <DropdownMenuItem 
                  onClick={() => handleStatusChange(row, 'Completed')}
                  className="text-green-400 focus:bg-green-500/10"
                >
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Mark Complete
                </DropdownMenuItem>
              )}
              {row.status === 'Not Started' && row.created_by === user?.id && (
                <DropdownMenuItem 
                  onClick={() => handleDelete(row)}
                  className="text-red-400 focus:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      }
    );

    return baseColumns;
  };

  const columns = getColumns();

  return (
    <div data-testid="tasks-page">
      <PageHeader
        title="Tasks"
        description="Manage and track all tasks"
        action={() => setShowCreateModal(true)}
        actionLabel="Create Task"
        actionIcon={Plus}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900/50 border border-slate-800 rounded-sm p-4">
          <p className="text-slate-400 text-sm">Total Tasks</p>
          <p className="text-2xl font-bold text-white">{stats.created_count + stats.assigned_count}</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
          <p className="text-blue-400 text-sm">In Progress</p>
          <p className="text-2xl font-bold text-white">{stats.by_status?.['In Progress'] || 0}</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4">
          <p className="text-green-400 text-sm">Completed</p>
          <p className="text-2xl font-bold text-white">{stats.by_status?.['Completed'] || 0}</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
          <p className="text-red-400 text-sm">Overdue</p>
          <p className="text-2xl font-bold text-white">{stats.overdue}</p>
        </div>
      </div>

      {/* Tabs for Created vs Allocated Tasks */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPagination(prev => ({ ...prev, page: 1 })); }} className="mb-6">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger 
            value="created" 
            className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900 gap-2"
          >
            <FileEdit className="w-4 h-4" />
            Created by Me
            <span className="ml-1 px-2 py-0.5 rounded-full bg-slate-700 text-xs data-[state=active]:bg-green-600">
              {stats.created_count}
            </span>
          </TabsTrigger>
          <TabsTrigger 
            value="assigned" 
            className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900 gap-2"
          >
            <ClipboardList className="w-4 h-4" />
            Allocated to Me
            <span className="ml-1 px-2 py-0.5 rounded-full bg-slate-700 text-xs data-[state=active]:bg-green-600">
              {stats.assigned_count}
            </span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <SearchFilter
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search tasks..."
        filters={[
          {
            key: 'status',
            placeholder: 'Status',
            value: status,
            onChange: setStatus,
            options: [
              { value: 'all', label: 'All Status' },
              ...STATUSES.map(s => ({ value: s, label: s }))
            ],
          },
          {
            key: 'priority',
            placeholder: 'Priority',
            value: priority,
            onChange: setPriority,
            options: [
              { value: 'all', label: 'All Priority' },
              ...PRIORITIES.map(p => ({ value: p, label: p }))
            ],
          },
        ]}
        onReset={() => { setSearch(''); setStatus('all'); setPriority('all'); }}
      />

      <DataTable
        columns={columns}
        data={tasks}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        emptyMessage="No tasks found"
      />

      {/* Create Task Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Title *</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Task title"
                className="bg-slate-800 border-slate-700"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description *</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed description..."
                className="bg-slate-800 border-slate-700 min-h-[80px]"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <Select value={newTask.category} onValueChange={(v) => setNewTask(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Assign To</Label>
              <Select 
                value={newTask.assigned_to_ids[0] || ''} 
                onValueChange={(v) => setNewTask(prev => ({ ...prev, assigned_to_ids: v ? [v] : [] }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.first_name} {e.last_name} - {e.designation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Start Date</Label>
                <Input
                  type="date"
                  value={newTask.planned_start_date}
                  onChange={(e) => setNewTask(prev => ({ ...prev, planned_start_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Due Date</Label>
                <Input
                  type="date"
                  value={newTask.planned_end_date}
                  onChange={(e) => setNewTask(prev => ({ ...prev, planned_end_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Estimated Hours</Label>
              <Input
                type="number"
                value={newTask.estimated_hours}
                onChange={(e) => setNewTask(prev => ({ ...prev, estimated_hours: parseFloat(e.target.value) || 0 }))}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="bg-green-500 hover:bg-green-600 text-slate-900">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Task
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-slate-500 font-mono text-sm">{selectedTask?.task_id}</span>
              {selectedTask?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(selectedTask?.status)}`}>
                {selectedTask?.status}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(selectedTask?.priority)}`}>
                {selectedTask?.priority}
              </span>
              <span className="text-xs text-slate-400 px-2 py-0.5 bg-slate-800 rounded">
                {selectedTask?.category}
              </span>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-2">Description</h4>
              <p className="text-slate-300 bg-slate-800 p-3 rounded-sm">{selectedTask?.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">Assigned To</h4>
                <p className="text-white">
                  {selectedTask?.assigned_to?.map(a => a.employee_name).join(', ') || 'Unassigned'}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">Progress</h4>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: `${selectedTask?.progress}%` }} />
                  </div>
                  <span className="text-white">{selectedTask?.progress}%</span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">Estimated</h4>
                <p className="text-white">{selectedTask?.estimated_hours || 0} hours</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">Logged</h4>
                <p className="text-white">{selectedTask?.actual_hours_logged || 0} hours</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">Start Date</h4>
                <p className="text-white">{selectedTask?.planned_start_date ? formatDate(selectedTask.planned_start_date) : '-'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">Due Date</h4>
                <p className="text-white">{selectedTask?.planned_end_date ? formatDate(selectedTask.planned_end_date) : '-'}</p>
              </div>
            </div>

            {selectedTask?.updates?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-2">Updates</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedTask.updates.map((u, i) => (
                    <div key={i} className="bg-slate-800 p-2 rounded-sm text-sm">
                      <p className="text-slate-300">{u.content}</p>
                      <p className="text-xs text-slate-500 mt-1">{u.updated_by_name} • {formatDate(u.created_at)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
